import React, { useEffect, useState } from 'react'
import { Dimmer, Loader, Modal } from 'semantic-ui-react'
import moment from 'moment';
import { createCalendarEvent, updateCalendarEvent } from '../../services/Calendar'
import sendToast from '../../../utils/Toast';
import { CloseIcon } from '../icons';
import '../../pages/calendar/style.less';

const DURATION_PRESETS = [
    { label: '30 min', mins: 30 },
    { label: '1 hr', mins: 60 },
    { label: '1.5 hr', mins: 90 },
    { label: '2 hr', mins: 120 },
];

const parseEventStart = (date?: string, startTime?: string) => {
    if (!date || !startTime) return moment();
    const parsed = moment(`${date} ${startTime}`, ['M/D/YYYY hh:mm A', 'M/D/YYYY h:mm A']);
    return parsed.isValid() ? parsed : moment();
};

const CreateCalendarEventModal = ({
    closeCreateEventModal,
    openCreateCalendarModal,
    createEventInfo,
    setRefreshCalendarEvents,
    refreshCalendarEvents,
    isEditingResidentEvent = false,
    eventToEdit,
}) => {
    const [event, setEvent] = useState<String>("")
    const [location, setLocation] = useState<String>("")
    const [startDateTimeString, setStartDateTimeString] = useState(() => moment(new Date()).format('YYYY-MM-DDTHH:mm:ss'));
    const [duration, setDuration] = useState<number>();
    const [isCreatingEvent, setIsCreatingEvent] = useState(false);

    useEffect(() => {
        if (!openCreateCalendarModal) return;
        if (isEditingResidentEvent && eventToEdit) {
            const start = parseEventStart(eventToEdit.date, eventToEdit.startTime);
            setEvent(eventToEdit.title || '');
            setLocation(eventToEdit.location || '');
            setStartDateTimeString(start.format('YYYY-MM-DDTHH:mm:ss'));
            let mins = eventToEdit.durationMinutes;
            if (!mins && eventToEdit.endDateTimeString) {
                const end = moment(eventToEdit.endDateTimeString, 'YYYY-MM-DDTHH:mm:ss');
                const diff = end.isValid() ? end.diff(start, 'minutes') : 0;
                if (diff > 0) mins = diff;
            }
            setDuration(mins || undefined);
            return;
        }
        setEvent('');
        setLocation('');
        setStartDateTimeString(moment(new Date()).format('YYYY-MM-DDTHH:mm:ss'));
        setDuration(undefined);
    }, [openCreateCalendarModal, isEditingResidentEvent, eventToEdit]);

    const handleEventChnage= (e: any) => {
        setEvent(e.target.value)
    }

    const handleLocationChange = (e: any) => {  
        setLocation(e.target.value)
    }

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (!value) return;
        setStartDateTimeString(moment(value).format('YYYY-MM-DDTHH:mm:ss'));
    };

    const handleSaveEvent = async () => {
        const isEditing = Boolean(isEditingResidentEvent && eventToEdit?.attendingId);
        try {
            setIsCreatingEvent(true);
            if (String(event).trim() === "" ||
                String(location).trim() === "" ||
                !event ||
                !startDateTimeString ||
                !location ||
                !createEventInfo.facilityId ||
                (!isEditing && !createEventInfo.registrantIds)) {
                sendToast('error', 'Please fill all the required fields.');
                return;
            }
            let endTime: string;
            if (duration) {
                endTime = moment(startDateTimeString).add(duration, 'm').format('YYYY-MM-DDTHH:mm:ss');
            } else if (isEditing && eventToEdit?.endDateTimeString) {
                const originalStart = parseEventStart(eventToEdit.date, eventToEdit.startTime);
                const originalEnd = moment(eventToEdit.endDateTimeString, 'YYYY-MM-DDTHH:mm:ss');
                const originalDuration = originalEnd.isValid() ? originalEnd.diff(originalStart, 'minutes') : 0;
                endTime = originalDuration > 0
                    ? moment(startDateTimeString).add(originalDuration, 'm').format('YYYY-MM-DDTHH:mm:ss')
                    : eventToEdit.endDateTimeString;
            } else {
                endTime = moment(startDateTimeString).add(30, 'm').format('YYYY-MM-DDTHH:mm:ss');
            }

            if (duration && duration > 1440 || !moment(endTime, 'YYYY-MM-DDTHH:mm:ss', true).isValid()) {
                sendToast('error', 'Event duration should be less than 24 hours.');
                return;
            }
            const dateTimeString = moment(startDateTimeString).format('YYYY-MM-DDTHH:mm:ss');
            let saveResponse;
            if (isEditing) {
                const originalRegistrantIds = (eventToEdit.registrantIds || []).map(String).filter(Boolean);
                saveResponse = await updateCalendarEvent({
                    facilityId: createEventInfo.facilityId,
                    event: {
                        _id: String(eventToEdit.attendingId),
                        text: String(event).trim(),
                        dateTimeString,
                        endDateTimeString: endTime,
                        location: String(location).trim(),
                        ...(originalRegistrantIds.length ? { registrantIds: originalRegistrantIds } : {}),
                    },
                });
            } else {
                saveResponse = await createCalendarEvent({
                    facilityId: createEventInfo.facilityId,
                    event: {
                        dateTimeString,
                        endDateTimeString: endTime,
                        registrantIds: (createEventInfo.registrantIds || []).map(String),
                        text: String(event).trim(),
                        staffId: createEventInfo.staffId,
                        location: String(location).trim(),
                    },
                });
            }
            if (saveResponse) {
                sendToast('success', isEditing ? 'Event updated successfully' : 'Event Created Successfully');
                closeCreateEventModal();
                setRefreshCalendarEvents(!refreshCalendarEvents);
            } else {
                sendToast('error', isEditing ? 'Error updating event' : 'Error creating event');
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : (isEditing ? 'Error updating event' : 'Error creating event'));
        } finally {
            setIsCreatingEvent(false);
        }
    }
    
    const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        // Remove decimal values
        const sanitizedValue = value.replace(/[^\d]/g, '');
        const mins = parseInt(sanitizedValue, 10);
        setDuration(mins);
    }

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSaveEvent();
    };

    return ( 
        <>
            <Modal
                open={openCreateCalendarModal}
                onClose={closeCreateEventModal}
                className="create-cal-event-modal"
            >
                <Dimmer active={isCreatingEvent} inverted>
                    <Loader  />
                </Dimmer>
                <Modal.Header className="create-cal-header">
                    <div className="create-cal-header-text">
                        <div className="create-cal-title min-w-0 break-words text-nbp-20 sm:text-nbp-24">{isEditingResidentEvent ? 'Edit Event' : 'Add to your Calendar'}</div>
                    </div>
                    <button
                        type="button"
                        className="create-cal-close"
                        onClick={closeCreateEventModal}
                        aria-label="Close"
                    >
                        <CloseIcon size={17} />
                    </button>
                </Modal.Header>
                <Modal.Content className="create-cal-body">
                    <form className="create-cal-form" onSubmit={handleFormSubmit}>
                        <div className="create-cal-field">
                            <label className="create-cal-label" htmlFor="calEventName">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                Event Name<span className="create-cal-req">*</span>
                            </label>
                            <textarea
                                className="create-cal-textarea"
                                id="calEventName"
                                placeholder="What's the occasion?"
                                rows={2}
                                value={String(event)}
                                onChange={handleEventChnage}
                            />
                        </div>

                        <div className="create-cal-field">
                            <label className="create-cal-label" htmlFor="calEventDate">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="3"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                Event Date<span className="create-cal-req">*</span>
                            </label>
                            <div className="create-cal-date">
                                <input
                                    className="create-cal-input"
                                    type="datetime-local"
                                    id="calEventDate"
                                    value={startDateTimeString ? moment(startDateTimeString).format('YYYY-MM-DDTHH:mm') : ''}
                                    onChange={handleDateChange}
                                />
                            </div>
                        </div>

                        <div className="create-cal-field">
                            <label className="create-cal-label" htmlFor="calDuration">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/></svg>
                                Duration
                            </label>
                            <div className="create-cal-duration-row flex min-w-0 w-full flex-wrap items-center gap-nbp-12">
                                <input
                                    className="create-cal-input w-full min-w-0 flex-shrink-0 sm:w-auto sm:max-w-nbp-90"
                                    type="number"
                                    id="calDuration"
                                    min="0"
                                    step="1"
                                    value={duration || ''}
                                    onChange={handleDurationChange}
                                    onKeyPress={(e) => {
                                        // Prevent typing of ".", ",", "-" or any other non-digit characters
                                        if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === '+') {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                <div className="create-cal-duration-presets flex w-full flex-wrap gap-nbp-8 sm:w-auto">
                                    {DURATION_PRESETS.map((preset) => (
                                        <button
                                            key={preset.mins}
                                            type="button"
                                            className={`create-cal-preset-btn${duration === preset.mins ? ' active' : ''}`}
                                            onClick={() => setDuration(preset.mins)}
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="create-cal-field">
                            <label className="create-cal-label" htmlFor="calLocation">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                Location<span className="create-cal-req">*</span>
                            </label>
                            <input
                                className="create-cal-input"
                                type="text"
                                id="calLocation"
                                placeholder="Where's it happening?"
                                value={String(location)}
                                onChange={handleLocationChange}
                            />
                        </div>
                    </form>
                </Modal.Content>
                <Modal.Actions className="create-cal-footer">
                    <button type="button" className="create-cal-submit" onClick={handleSaveEvent}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="3"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
                        Save Event
                    </button>
                </Modal.Actions>
            </Modal>
        </>
    )
}

export default CreateCalendarEventModal
